import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

/**
 * InvestorMetrics — system-admin investor dashboard.
 *
 * Renders the FERPA-compliant aggregate data returned by
 * GET /api/admin/investor/metrics. No PII reaches this component.
 *
 * Sections:
 *   1. Top-line growth tiles + 12-week sparkline
 *   2. Viral coefficient + share funnel + top inviters (anonymized)
 *   3. Activation funnel (signup → onboarding → class → student → session)
 *   4. Cohort retention table (placeholder cells until 12 weeks of data)
 *   5. School clusters (top schools + districts with 3+ teachers)
 *   6. Acquisition channel mix
 *   9b. Unit economics (placeholder until billing pipeline ships)
 *   "All users" — global anonymized counts, including pre-attribution users
 */

const PERIODS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'year', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

const card = {
  background: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 20,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

const sectionHeader = {
  fontSize: 14,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 12,
};

const Stat = ({ label, value, sub, accent = '#1f2937' }) => (
  <div style={card}>
    <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div style={{ fontSize: 28, fontWeight: 700, color: accent, marginTop: 6 }}>
      {value}
    </div>
    {sub && (
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
        {sub}
      </div>
    )}
  </div>
);

const fmtNum = (n) =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString();
const fmtPct = (n) =>
  n === null || n === undefined ? '—' : `${Number(n).toFixed(1)}%`;
const fmtDelta = (n) => {
  if (n === null || n === undefined) return '';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${Number(n).toFixed(1)}%`;
};
const fmtUsd = (n) =>
  n === null || n === undefined ? 'Pending' : `$${Number(n).toFixed(2)}`;

const InvestorMetrics = () => {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `/api/admin/investor/metrics?period=${encodeURIComponent(period)}`
      );
      setData(res.data);
    } catch (err) {
      setError(
        err.response?.data?.error || 'Could not load investor metrics.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  if (loading && !data) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Loading investor metrics…</div>;
  }
  if (error) {
    return (
      <div style={{ ...card, borderColor: '#fee2e2', background: '#fef2f2' }}>
        <strong>Couldn't load:</strong> {error}
      </div>
    );
  }
  if (!data) return null;

  const growth = data.growth || {};
  const viral = data.viral || {};
  const activation = data.activation || {};
  const retention = data.retention || {};
  const schools = data.schools || {};
  const channels = data.channels || {};
  const ue = data.unit_economics || {};
  const usersTotal = data.users_total || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {/* Period selector + meta */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              fontSize: 14,
              background: 'white',
            }}
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af' }}>
          Generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : ''}
          {' · '}
          <span style={{ color: '#10b981' }}>FERPA-safe (counts only)</span>
        </div>
      </div>

      {/* Section 1: Top-line growth */}
      <section>
        <div style={sectionHeader}>Top-line growth</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <Stat
            label="Teachers (total)"
            value={fmtNum(growth.teachers?.total)}
            sub={`Free ${fmtNum(growth.teachers?.free)} · Paid ${fmtNum(growth.teachers?.paid)}`}
          />
          <Stat
            label={`New teachers (${data.period})`}
            value={fmtNum(growth.teachers?.new_in_period)}
            sub={fmtDelta(growth.teachers?.delta_pct) + ' vs prior period'}
            accent={growth.teachers?.delta_pct >= 0 ? '#10b981' : '#ef4444'}
          />
          <Stat label="Students (total)" value={fmtNum(growth.students?.total)} />
          <Stat label="Mira sessions (total)" value={fmtNum(growth.sessions?.total)} />
        </div>
        {Array.isArray(growth.teachers?.weekly_sparkline) &&
          growth.teachers.weekly_sparkline.length > 0 && (
            <div style={{ ...card, marginTop: 12 }}>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
                New teacher signups per week (last 12 weeks)
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={growth.teachers.weekly_sparkline}>
                  <Line type="monotone" dataKey="count" stroke="#297A92" strokeWidth={2} dot={false} />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
      </section>

      {/* Section 2: Viral coefficient */}
      <section>
        <div style={sectionHeader}>Viral coefficient — bottom-up distribution</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
          <Stat label="k-factor" value={fmtNum(viral.k_factor)} sub="Signups ÷ active teachers" />
          <Stat label="Share clicks" value={fmtNum(viral.shares)} />
          <Stat label="Visits" value={fmtNum(viral.visits)} />
          <Stat label="Signups" value={fmtNum(viral.signups)} sub={fmtPct(viral.visit_to_signup_pct) + ' visit→signup'} />
          <Stat label="Activated" value={fmtNum(viral.activated)} sub={fmtPct(viral.signup_to_activated_pct) + ' signup→activated'} />
        </div>
        {Array.isArray(viral.leaderboard_anonymized) && viral.leaderboard_anonymized.length > 0 && (
          <div style={{ ...card, marginTop: 12 }}>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
              Top inviters (anonymized — rank only)
            </div>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px' }}>Rank</th>
                  <th style={{ padding: '6px 8px' }}>Shares</th>
                  <th style={{ padding: '6px 8px' }}>Signups</th>
                  <th style={{ padding: '6px 8px' }}>Activated</th>
                </tr>
              </thead>
              <tbody>
                {viral.leaderboard_anonymized.map((row) => (
                  <tr key={row.rank} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '6px 8px' }}>#{row.rank}</td>
                    <td style={{ padding: '6px 8px' }}>{fmtNum(row.shares)}</td>
                    <td style={{ padding: '6px 8px' }}>{fmtNum(row.signups)}</td>
                    <td style={{ padding: '6px 8px' }}>{fmtNum(row.activated)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Section 3: Activation funnel */}
      <section>
        <div style={sectionHeader}>Activation funnel — free-tier teachers</div>
        <div style={card}>
          {Array.isArray(activation.stages) && activation.stages.length > 0 ? (
            activation.stages.map((s, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 0',
                  borderTop: idx === 0 ? 'none' : '1px solid #f3f4f6',
                }}
              >
                <div style={{ fontSize: 14, color: '#1f2937' }}>{s.name}</div>
                <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
                  <div style={{ fontSize: 13, color: '#9ca3af', minWidth: 60, textAlign: 'right' }}>
                    {fmtPct(s.pct_of_signups)}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#297A92', minWidth: 60, textAlign: 'right' }}>
                    {fmtNum(s.count)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ color: '#9ca3af' }}>No activation data yet.</div>
          )}
        </div>
      </section>

      {/* Section 4: Cohort retention */}
      <section>
        <div style={sectionHeader}>Weekly cohort retention</div>
        <div style={{ ...card, overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                <th style={{ padding: '8px' }}>Cohort week</th>
                <th style={{ padding: '8px' }}>Size</th>
                <th style={{ padding: '8px' }}>Wk +1</th>
                <th style={{ padding: '8px' }}>Wk +4</th>
                <th style={{ padding: '8px' }}>Wk +12</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(retention.cohorts) && retention.cohorts.length > 0 ? (
                retention.cohorts.map((c, idx) => (
                  <tr key={idx} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px' }}>{c.cohort_week}</td>
                    <td style={{ padding: '8px' }}>{fmtNum(c.size)}</td>
                    <td style={{ padding: '8px' }}>{c.wk1_active_pct === null ? '—' : fmtPct(c.wk1_active_pct)}</td>
                    <td style={{ padding: '8px' }}>{c.wk4_active_pct === null ? '—' : fmtPct(c.wk4_active_pct)}</td>
                    <td style={{ padding: '8px' }}>{c.wk12_active_pct === null ? '—' : fmtPct(c.wk12_active_pct)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ padding: '12px', color: '#9ca3af' }}>
                    Retention populates as cohorts mature. Real percentages start filling Week +1 after launch.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Section 5: School clusters */}
      <section>
        <div style={sectionHeader}>School clusters — Phase-2 sales targets</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Stat label="Schools with ≥1 teacher" value={fmtNum(schools.schools_total_with_teachers)} />
          <Stat label="Schools with ≥3 teachers" value={fmtNum(schools.districts_with_3plus_teachers)} accent="#10b981" />
          <Stat label="Top school size" value={fmtNum(schools.top_schools?.[0]?.teacher_count)} sub={schools.top_schools?.[0]?.school_name} />
        </div>
        {Array.isArray(schools.top_schools) && schools.top_schools.length > 0 && (
          <div style={{ ...card, marginTop: 12 }}>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
              Top 25 schools by teacher count
            </div>
            <ResponsiveContainer width="100%" height={Math.min(360, 24 * schools.top_schools.length + 40)}>
              <BarChart data={schools.top_schools} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                <YAxis dataKey="school_name" type="category" width={180} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="teacher_count" fill="#297A92" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* Section 6: Acquisition channels */}
      <section>
        <div style={sectionHeader}>Acquisition channels</div>
        <div style={card}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                <th style={{ padding: '8px' }}>Source</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Signups</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Activated</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Activation rate</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(channels.channels) && channels.channels.length > 0 ? (
                channels.channels.map((c) => (
                  <tr key={c.source} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px' }}>{c.source === '_unknown' ? 'Not provided' : c.source}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{fmtNum(c.signups)}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{fmtNum(c.activated)}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{fmtPct(c.activation_pct)}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={4} style={{ padding: '12px', color: '#9ca3af' }}>No channel data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Section 9b: Unit economics */}
      <section>
        <div style={sectionHeader}>Unit economics (Rule of 40 inputs)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Stat
            label="Cost / active teacher / mo"
            value={fmtUsd(ue.cost_per_active_teacher_usd)}
            sub={ue._provenance ? 'Pending billing-pipeline wire-up' : null}
          />
          <Stat
            label="Cost / Mira session"
            value={fmtUsd(ue.cost_per_session_usd)}
            sub="Computable from inference bills (v2)"
          />
          <Stat
            label="Gross margin"
            value={ue.gross_margin_pct === null ? 'Pending' : fmtPct(ue.gross_margin_pct)}
            sub={ue.cac_note}
          />
        </div>
      </section>

      {/* All users — global anonymized */}
      <section>
        <div style={sectionHeader}>All users (global, anonymized)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <Stat label="Total accounts" value={fmtNum(usersTotal.total)} sub="Including pre-attribution era" />
          <Stat label="Institutional" value={fmtNum(usersTotal.institutional)} sub="Has school_license_id" />
          <Stat label="Non-institutional" value={fmtNum(usersTotal.non_institutional)} />
          <Stat label="Active in last 30d" value={fmtNum(usersTotal.active_30d)} sub="Had ≥1 session" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div style={card}>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>By role</div>
            <CountTable counts={usersTotal.by_role} />
          </div>
          <div style={card}>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>By tier</div>
            <CountTable counts={usersTotal.by_tier} />
          </div>
        </div>
        {Array.isArray(usersTotal.monthly_signups_12mo) && usersTotal.monthly_signups_12mo.length > 0 && (
          <div style={{ ...card, marginTop: 12 }}>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
              Signups per month (last 12 months)
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={usersTotal.monthly_signups_12mo}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="signups" fill="#297A92" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', paddingBottom: 24 }}>
        {data._ferpa_note}
      </div>
    </div>
  );
};

const CountTable = ({ counts }) => {
  if (!counts || Object.keys(counts).length === 0) {
    return <div style={{ color: '#9ca3af', fontSize: 13 }}>No data.</div>;
  }
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const total = sorted.reduce((s, [, v]) => s + Number(v || 0), 0);
  return (
    <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
      <tbody>
        {sorted.map(([key, value]) => (
          <tr key={key} style={{ borderTop: '1px solid #f3f4f6' }}>
            <td style={{ padding: '6px 8px' }}>{key}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{fmtNum(value)}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#9ca3af', width: 60 }}>
              {total > 0 ? fmtPct((value / total) * 100) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default InvestorMetrics;
